import React, { useRef, useCallback, useState, useEffect } from 'react';
import { useEditor } from '../contexts/EditorContext';

/**
 * InlineText — click-to-edit text on the live page when the CMS editor is active.
 *
 * Props:
 *  cmsKey    – content_overrides key  (required)
 *  fallback  – default text when no CMS value exists
 *  as        – HTML tag to render (default "span")
 *  className / style / data-testid – passed through
 */
export function InlineText({ cmsKey, fallback = '', as: Tag = 'span', className = '', style, ...rest }) {
  const { isActive, getValue, setValue } = useEditor();
  const value = getValue ? getValue(cmsKey, fallback) : fallback;
  const ref = useRef(null);
  const [editing, setEditing] = useState(false);

  // Sync DOM text with value when not editing
  useEffect(() => {
    if (!editing && ref.current && ref.current.textContent !== value) {
      ref.current.textContent = value;
    }
  }, [value, editing]);

  const handleBlur = useCallback(() => {
    setEditing(false);
    if (!ref.current || !setValue) return;
    const newText = ref.current.textContent || '';
    if (newText !== value) {
      setValue(cmsKey, newText);
    }
  }, [cmsKey, value, setValue]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      // Revert to original value
      if (ref.current) ref.current.textContent = value;
      ref.current?.blur();
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      ref.current?.blur();
    }
  }, [value]);

  const startEdit = useCallback((e) => {
    if (!isActive) return;
    e.stopPropagation();
    e.preventDefault();
    setEditing(true);
    requestAnimationFrame(() => {
      if (ref.current) {
        ref.current.focus();
        const sel = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(ref.current);
        range.collapse(false);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    });
  }, [isActive]);

  // Non-editor mode: plain render
  if (!isActive) {
    return <Tag className={className} style={style} {...rest}>{value}</Tag>;
  }

  return (
    <Tag
      ref={ref}
      className={`${className} vw-inline-editable ${editing ? 'vw-inline-editing' : ''}`}
      style={style}
      contentEditable={editing}
      suppressContentEditableWarning
      onClick={startEdit}
      onBlur={handleBlur}
      onKeyDown={editing ? handleKeyDown : undefined}
      data-cms-key={cmsKey}
      {...rest}
    >
      {value}
    </Tag>
  );
}

export default InlineText;
